import {
  APP_3in1,
  APP_M1,
  APP_SRC,
  COMMON_SSO_ACCESS_TOKEN,
  SOURCE,
  WEB_LOGGED_IN,
  WEB_NON_LOGGED_IN,
  WEB_NON_LOGGED_IN_IDENTIFIED,
} from "../constants/sso.constants";
// Lazily loads the crypto module (crypto-js, ~125 KiB) only when an SSO or
// encryption operation is first needed.  This keeps crypto-js out of the
// initial JS bundle so it does not block first-page render.
let _cryptoMod: Promise<typeof import("./crypto")> | null = null;

// Detect if we're running in Jest environment
const isJestWorker = () => {
  if (typeof process === "undefined") return false;
  // Check multiple Jest indicators for reliability
  return !!(
    process.env.JEST_WORKER_ID ||
    process.env.JEST ||
    (typeof global !== "undefined" && (global as any).__JEST__) ||
    typeof jest !== "undefined"
  );
};

const loadCrypto = async () => {
  // Jest workers in Node 22 can throw ERR_VM_DYNAMIC_IMPORT_CALLBACK_MISSING_FLAG
  // for dynamic import(); use CJS require in that environment only.
  // Also wrap in try-catch to handle edge cases where dynamic import fails.
  if (isJestWorker()) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      return require("./crypto") as typeof import("./crypto");
    } catch (_e) {
      // If require fails, fall through to dynamic import
    }
  }

  try {
    // Use string literal for dynamic import so esbuild can properly bundle it
    return (_cryptoMod ??= import("./crypto.ts"));
  } catch (_e) {
    // Last resort: try require if dynamic import fails
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    return require("./crypto") as typeof import("./crypto");
  }
};
import { APP_PROMPTS } from "../constants/pwa.constants";
import sessionData from "../mock/loggedIn-mock.json";
import { isOpenInApp, stringToBoolean } from "./pwa-utils";
import { LogUtils } from "./log-utils";
import { INVEST_NOW_MODAL_KEY } from "../constants/shared.constants";
const cache = new WeakMap();

type NodeLike = {
  nodeName?: string;
  children?: NodeLike[];
};

export type SSOURLConfigs = {
  fullUrl: string;
  newUrl: URL;
  ssoProduct: string[] | null;
  ssoModule: string | null;
  ssoResume: string | null;
  ssoFormPostUrl: string | null;
};

/**
 * Checks if a string or array has content
 * @param {string | Array<unknown>} value - The value to check
 * @returns {boolean} True if the value is not null, not undefined, and has length greater than 0
 * @example
 * doesExist("hello") // returns true
 * doesExist([1, 2, 3]) // returns true
 * doesExist("") // returns false
 * doesExist([]) // returns false
 */
export const doesExist = (value: string | null | undefined | Array<unknown>) => {
  return value !== null && value !== undefined && value?.length > 0;
};

/**
 * Checks if an object has properties
 * @param {unknown} value - The object to check
 * @returns {boolean} True if the value is not null, not undefined, and has at least one property
 * @example
 * isNotEmptyObject({ key: "value" }) // returns true
 * isNotEmptyObject({}) // returns false
 */
export const isNotEmptyObject = (value: unknown = {}) => {
  return value !== null && value !== undefined && Object.keys(value).length > 0;
};

/**
 * Determines if a given URL path should be handled by Next.js routing
 * @param {string} pageUrl - The URL path to check
 * @returns {boolean} True if the URL should be handled by Next.js, false otherwise
 * @example
 * isNextPage("/investments/stock-name") // returns true
 * isNextPage("/investments/fund-name-growth?next=true") // returns true
 * isNextPage("/anything-for-now/") // returns false
 */
export const isNextPage = (pageUrl: string) => {
  // add regexps to match for urls
  const matchPatterns = [
    /^\/?investments\/[^/]+-growth$/i,
    /^\/?app-[^/]+\/investments\/[^/]+-growth$/i,
    /^\/?investments\/[^/]+-share-price$/i,
    /^\/?investments\/[^/]+-growth\?next=true$/i,
    /^\/?app-[^/]+\/investments\/[^/]+-growth\?next=true$/i,
  ];
  const notMatchPatterns = [/^\/anything-for-now\/$/];

  const matches = matchPatterns.some((pattern) => pattern.test(pageUrl));

  const notMatches = notMatchPatterns.some((pattern) => pattern.test(pageUrl));

  return matches && !notMatches;
};

/**
 * Checks if code is running in a browser environment
 * @returns {boolean} True if running in a browser, false otherwise
 * @example
 * if (isBrowser()) {
 *   // Execute browser-specific code
 * }
 */
export const isBrowser = () => {
  return globalThis.window !== undefined;
};

/**
 * Gets a URL parameter value from the current window location
 * @param {string} paramsValue - The name of the parameter to retrieve
 * @returns {string | null} The value of the parameter if found, null otherwise
 * @example
 * // For URL: https://example.com?tab=profile
 * getParams("tab") // returns "profile"
//  */
export const normalizeUrlParams = (url: string): string => {
  try {
    const urlObj = new URL(url);
    if (urlObj.search) {
      urlObj.search = urlObj.search.replace(/\^|%5e/gi, "&");
    }
    return urlObj.toString();
  } catch (error) {
    console.warn("URL parsing failed, using fallback method:", error);
    return url.replace(/\^|%5e/gi, "&");
  }
};

export const getParams = (paramsValue: string): string | null => {
  const currentUrl = window.location.href;
  const url = new URL(currentUrl);
  const searchLower = url.search.toLowerCase();
  if (url.search.includes("^") || searchLower.includes("%5e")) {
    const normalizedUrl = normalizeUrlParams(currentUrl);
    const normalizedUrlObj = new URL(normalizedUrl);
    return normalizedUrlObj.searchParams.get(paramsValue);
  }
  const params = url.searchParams;
  return params.get(paramsValue);
};

/**
 * Smoothly scrolls the window to a specified position
 * @param {number} [top=0] - The Y position to scroll to (in pixels)
 * @param {ScrollBehavior} [behavior="smooth"] - The scroll behavior ("auto" | "smooth")
 * @returns {void}
 * @example
 * scrollTo(0) // Scrolls to top smoothly
 * scrollTo(500, "auto") // Jumps to 500px position
 */
export const scrollTo = (
  top: number = 0,
  behavior: ScrollBehavior | undefined = "smooth"
): void => {
  if (isBrowser()) {
    globalThis.window.scrollTo({ top, behavior });
  }
};

/**
 * Converts an object with keys like "item0", "item1", etc.,
 * into a sorted array based on numeric suffix of the keys.
 *
 * @param {Object} unorderedObj - Object with keys like "item0", "item2", etc.
 * @param {string} keyPrefix - Common prefix used in the keys (e.g., "item")
 * @returns {Array} - Sorted array of object values
 */
export const sortObjectByKeySuffix = (
  unorderedObj: Record<string, unknown>,
  keyPrefix = "item"
) => {
  if (!unorderedObj || typeof unorderedObj !== "object") return [];

  return Object.keys(unorderedObj)
    .sort((a, b) => {
      const numA = Number.parseInt(a.replace(keyPrefix, ""), 10);
      const numB = Number.parseInt(b.replace(keyPrefix, ""), 10);
      return numA - numB;
    })
    .map((key) => unorderedObj[key]);
};
export const formatDateWithFullYear = (input: string) => {
  const year = input?.slice(0, 4);
  const month = input?.slice(4);
  const date = new Date(Number.parseInt(year), Number.parseInt(month) - 1);
  const formatted = date.toLocaleString("default", {
    month: "short",
    year: "numeric",
  });
  return formatted;
};

/**
 * Sets a cookie with the specified name, value, and optional parameters
 * @param {string} name - The name of the cookie
 * @param {string} value - The value of the cookie
 * @param {number} [days] - Optional number of days until the cookie expires
 * @param {string} [path="/"] - Optional path for the cookie
 */
export const setCookie = (cookieName: string, time: number, value: string): void => {
  const date = new Date();
  let expires: string;
  if (time > 0) {
    const cookieTime = time;
    date.setTime(date.getTime() + cookieTime * 60 * 1000);
    expires = `expires=${date.toUTCString()}`;
    document.cookie = `${cookieName}=${value}; path=/; domain=.bajajfinserv.in;  ${expires};`;
  } else {
    document.cookie = `${cookieName}=${value}; path=/; domain=.bajajfinserv.in;`;
  }
};

/**
 * Deletes a cookie with the specified name
 * @param {string} name - The name of the cookie to delete
 * @param {string} [path="/"] - Optional path of the cookie
 */
export const deleteCookie = (
  name: string,
  path: string = "/",
  domain: string = ".bajajfinserv.in"
): void => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=${path}; domain=${domain}`;
};

/**
 * Gets the value of a cookie by name
 * @param {string} name - The name of the cookie to retrieve
 * @returns {string | null} The cookie value if found, null otherwise
 */
export const getCookie = (name: string): string => {
  if (!isBrowser()) return "";
  const cookieName = name.replaceAll(/[.$?*|{}()[\]\\/+^]/g, String.raw`\$&`);
  const cookieRegex = new RegExp(String.raw`(?:^|; )${cookieName}=([^;]*)`);
  const matches = cookieRegex.exec(document.cookie);
  return matches?.[1] ? decodeURIComponent(matches[1]) : "";
};

export const toTitleCase = (str: string) => {
  return str
    ?.toLowerCase()
    ?.split(" ")
    ?.map((word) => word?.charAt(0).toUpperCase() + word.slice(1))
    ?.join(" ");
};

/** Uppercases scheme name for metadata; the word "fund" is rendered as "Fund". */
export const formatSchNameForMetadata = (str: string) => {
  if (!str) return "";
  return str.toUpperCase().replace(/\bFUND\b/g, "Fund");
};
/**
 * Generates a sorted array of entries from an object based on componentPostion property
 * @param obj - The input object containing components with componentPostion property
 * @returns An array of sorted entries where each entry is a tuple of [key, value]
 */
type ComponentPositionLike = Record<string, unknown> & {
  componentPosition?: string | number;
  componentPostion?: string | number; // typo exists in actual data
};

export const generateSortedComponentList = (obj: Record<string, Record<string, unknown>>) => {
  const sortedList = Object.entries(obj).sort(([, a], [, b]) => {
    const aPos =
      (a as ComponentPositionLike)?.componentPosition ||
      (a as ComponentPositionLike)?.componentPostion;
    const bPos =
      (b as ComponentPositionLike)?.componentPosition ||
      (b as ComponentPositionLike)?.componentPostion;
    return Number(aPos) - Number(bPos);
  });
  return sortedList;
};

/**
 * Decodes HTML entities and special characters in a text string.
 *
 * @param text - The input string containing HTML entities and special characters to decode
 * @returns The decoded string with HTML entities and special characters converted to their actual representations
 *
 * @remarks
 * This function handles the following conversions:
 * - Removes double escaped newlines (\\r\\n)
 * - Removes regular newlines (\r\n)
 * - &quot; to "
 * - &nbsp; to space
 * - &lt; to <
 * - &gt; to >
 * - &amp; to &
 * - \u003C to <
 * - \u003E to >
 */
export const decodeHtmlEntities = (text: string) => {
  if (!text) return "";
  if (text.length > 200000) {
    throw new Error("Input too long");
  }

  let decoded = text
    .replaceAll("&quot;", '"')
    .replaceAll("&nbsp;", " ")
    .replaceAll("&lt;", "<")
    .replaceAll("&gt;", ">")
    .replaceAll("&amp;", "&")
    .replaceAll(String.raw`\u003C`, "<")
    .replaceAll(String.raw`\u003E`, ">")

    // Convert escaped newlines to real ones first
    .replaceAll(String.raw`\r\n`, "\n")
    .replaceAll(String.raw`\n`, "\n")
    .replaceAll(String.raw`\r`, "")
    .replaceAll("\r", "")

    // Remove empty or meaningless paragraphs
    .replaceAll(/<p>\s*<\/p>/gi, "")

    // Replace real newlines (now cleaned) with <br/> only when not next to a tag
    .replaceAll(/(?<!>)\n(?!<)/g, "<br/>")

    // add line break and ensure exactly two asterisks
    .replaceAll(/([a-z0-9>])\s*\*{2,}\s*Disclaimer/gi, "$1<br/>**Disclaimer")
    .trim();

  // Collapse repeated <br/> in bounded passes to avoid ambiguous quantified regex.
  for (let i = 0; i < 5; i += 1) {
    const next = decoded.replaceAll(/<br\s*\/?>\s*<br\s*\/?>/gi, "<br/>");
    if (next === decoded) {
      break;
    }
    decoded = next;
  }

  return decoded;
};

/**
 * Sanitizes HTML fragments before rendering via dangerouslySetInnerHTML.
 * Removes executable tags and inline event handlers while preserving simple formatting tags.
 */
export const sanitizeHtmlForRender = (html: string): string => {
  if (!html) return "";
  if (html.length > 200000) {
    throw new Error("Input too long");
  }

  const unsafeTagNames = new Set([
    "script",
    "style",
    "iframe",
    "object",
    "embed",
    "form",
    "meta",
    "link",
    "base",
  ]);

  const isBlockedUriScheme = (value: string): boolean => {
    const decodeHtmlEntityRefs = (input: string): string => {
      return input
        .replaceAll("&colon;", ":")
        .replaceAll(/&#x([0-9a-f]+);?/gi, (_full, hex: string) => {
          const codePoint = Number.parseInt(hex, 16);
          return Number.isNaN(codePoint) ? _full : String.fromCodePoint(codePoint);
        })
        .replaceAll(/&#(\d+);?/g, (_full, decimal: string) => {
          const codePoint = Number.parseInt(decimal, 10);
          return Number.isNaN(codePoint) ? _full : String.fromCodePoint(codePoint);
        });
    };

    const removeControlAndWhitespace = (input: string): string => {
      let sanitized = "";
      for (const char of input) {
        const code = char.codePointAt(0) ?? 0;
        const isControlOrSpace = (code >= 0 && code <= 32) || code === 127;
        if (!isControlOrSpace) {
          sanitized += char;
        }
      }
      return sanitized;
    };

    const normalizeSchemeCandidate = (input: string): string => {
      return removeControlAndWhitespace(decodeHtmlEntityRefs(input)).toLowerCase();
    };

    const normalized = String(value || "")
      .trim()
      .toLowerCase();

    const candidates = [normalized];

    try {
      candidates.push(decodeURI(normalized));
    } catch {
      // Ignore decodeURI failures and continue with other candidates.
    }

    try {
      candidates.push(decodeURIComponent(normalized));
    } catch {
      // Ignore decodeURIComponent failures and continue with other candidates.
    }

    const allowedSchemes = new Set(["http", "https", "mailto", "tel", "bajajsuperapp"]);

    return candidates.some((candidate) => {
      const normalizedCandidate = normalizeSchemeCandidate(candidate);

      if (!normalizedCandidate) {
        return false;
      }

      // Allow relative references and protocol-relative URLs.
      if (
        normalizedCandidate.startsWith("/") ||
        normalizedCandidate.startsWith("./") ||
        normalizedCandidate.startsWith("../") ||
        normalizedCandidate.startsWith("?") ||
        normalizedCandidate.startsWith("#") ||
        normalizedCandidate.startsWith("//")
      ) {
        return false;
      }

      let schemeEnd = 0;
      while (schemeEnd < normalizedCandidate.length) {
        const char = normalizedCandidate[schemeEnd] ?? "";
        if (!char) {
          break;
        }

        if (char === ":") {
          break;
        }

        const isAlphaNum =
          (char >= "a" && char <= "z") ||
          (char >= "0" && char <= "9") ||
          char === "+" ||
          char === "." ||
          char === "-";

        if (!isAlphaNum) {
          return false;
        }

        schemeEnd += 1;
      }

      if (schemeEnd <= 0 || normalizedCandidate[schemeEnd] !== ":") {
        return false;
      }

      const scheme = normalizedCandidate.slice(0, schemeEnd);
      return !allowedSchemes.has(scheme);
    });
  };

  const isWhitespace = (char: string | undefined) => char === " " || char === "\t" || char === "\n";

  const parseTagName = (content: string): { tagName: string; nextCursor: number } | null => {
    let tagNameEnd = 0;
    while (tagNameEnd < content.length) {
      const char = content[tagNameEnd] ?? "";
      if (!char) {
        break;
      }
      const isAlphaNum =
        (char >= "a" && char <= "z") ||
        (char >= "A" && char <= "Z") ||
        (char >= "0" && char <= "9");
      if (!(isAlphaNum || char === ":" || char === "-")) {
        break;
      }
      tagNameEnd += 1;
    }

    if (tagNameEnd === 0) {
      return null;
    }

    return {
      tagName: content.slice(0, tagNameEnd).toLowerCase(),
      nextCursor: tagNameEnd,
    };
  };

  const isSelfClosingTagContent = (content: string): boolean => {
    let endCursor = content.length - 1;
    while (endCursor >= 0 && isWhitespace(content[endCursor])) {
      endCursor -= 1;
    }
    return endCursor >= 0 && content[endCursor] === "/";
  };

  const skipWhitespace = (content: string, startCursor: number): number => {
    let cursor = startCursor;
    while (cursor < content.length && isWhitespace(content[cursor])) {
      cursor += 1;
    }
    return cursor;
  };

  const readAttributeName = (
    content: string,
    startCursor: number
  ): { name: string; nextCursor: number } => {
    let cursor = startCursor;
    while (
      cursor < content.length &&
      !isWhitespace(content[cursor]) &&
      content[cursor] !== "=" &&
      content[cursor] !== "/"
    ) {
      cursor += 1;
    }
    return { name: content.slice(startCursor, cursor), nextCursor: cursor };
  };

  const readAttributeValue = (
    content: string,
    startCursor: number
  ): { value: string; nextCursor: number } => {
    let cursor = skipWhitespace(content, startCursor);

    if (cursor < content.length && (content[cursor] === '"' || content[cursor] === "'")) {
      const quote = content[cursor] as string;
      cursor += 1;
      const valueStart = cursor;
      while (cursor < content.length && content[cursor] !== quote) {
        cursor += 1;
      }
      const value = content.slice(valueStart, cursor);
      if (cursor < content.length) {
        cursor += 1;
      }
      return { value, nextCursor: cursor };
    }

    const valueStart = cursor;
    while (cursor < content.length && !isWhitespace(content[cursor]) && content[cursor] !== "/") {
      cursor += 1;
    }
    return { value: content.slice(valueStart, cursor), nextCursor: cursor };
  };

  const shouldDropAttribute = (attrName: string, attrValue: string | undefined): boolean => {
    const lowerAttrName = attrName.toLowerCase();
    if (lowerAttrName.startsWith("on")) {
      return true;
    }
    return (
      (lowerAttrName === "href" || lowerAttrName === "src") && isBlockedUriScheme(attrValue ?? "")
    );
  };

  const serializeAttribute = (attrName: string, attrValue: string | undefined): string => {
    if (attrValue === undefined) {
      return attrName;
    }
    const escapedValue = attrValue.replaceAll('"', "&quot;");
    return `${attrName}="${escapedValue}"`;
  };

  const readAttribute = (
    content: string,
    startCursor: number
  ): { name: string; value?: string; nextCursor: number } | null => {
    let cursor = skipWhitespace(content, startCursor);

    if (cursor >= content.length || content[cursor] === "/") {
      return null;
    }

    const nameRead = readAttributeName(content, cursor);
    const name = nameRead.name;
    cursor = nameRead.nextCursor;
    if (!name) {
      return { name: "", nextCursor: cursor + 1 };
    }

    cursor = skipWhitespace(content, cursor);

    if (cursor >= content.length || content[cursor] !== "=") {
      return { name, nextCursor: cursor };
    }

    const valueRead = readAttributeValue(content, cursor + 1);

    return {
      name,
      value: valueRead.value,
      nextCursor: valueRead.nextCursor,
    };
  };

  const sanitizeTagToken = (tagToken: string): string => {
    if (!tagToken.startsWith("<") || !tagToken.endsWith(">")) {
      return tagToken;
    }

    const inner = tagToken.slice(1, -1).trim();
    if (!inner) {
      return "";
    }

    const isClosingTag = inner.startsWith("/");
    const content = isClosingTag ? inner.slice(1).trim() : inner;

    const tagInfo = parseTagName(content);
    if (!tagInfo) {
      return tagToken;
    }

    const { tagName } = tagInfo;
    if (unsafeTagNames.has(tagName)) {
      return "";
    }

    if (isClosingTag) {
      return `</${tagName}>`;
    }

    const selfClosing = isSelfClosingTagContent(content);
    const attrs: string[] = [];
    let cursor = tagInfo.nextCursor;

    while (cursor < content.length) {
      const attr = readAttribute(content, cursor);
      if (!attr) {
        break;
      }
      cursor = attr.nextCursor;

      if (!attr.name) {
        continue;
      }
      if (shouldDropAttribute(attr.name, attr.value)) {
        continue;
      }

      attrs.push(serializeAttribute(attr.name, attr.value));
    }

    const attrsSuffix = attrs.length > 0 ? ` ${attrs.join(" ")}` : "";
    const closeSuffix = selfClosing ? "/" : "";
    return `<${tagName}${attrsSuffix}${closeSuffix}>`;
  };

  let result = "";
  let cursor = 0;

  while (cursor < html.length) {
    const openIndex = html.indexOf("<", cursor);
    if (openIndex === -1) {
      result += html.slice(cursor);
      break;
    }

    result += html.slice(cursor, openIndex);

    const closeIndex = html.indexOf(">", openIndex + 1);
    if (closeIndex === -1) {
      result += html.slice(openIndex);
      break;
    }

    const token = html.slice(openIndex, closeIndex + 1);
    result += sanitizeTagToken(token);
    cursor = closeIndex + 1;
  }

  return result;
};

export const extractPlainText = (html: string): string => {
  if (!html) return "";

  const stripHtmlComments = (value: string): string => {
    let result = value;
    let start = result.indexOf("<!--");

    while (start !== -1) {
      const end = result.indexOf("-->", start + 4);
      if (end === -1) {
        result = `${result.slice(0, start)}${result.slice(start + 4)}`;
      } else {
        result = `${result.slice(0, start)}${result.slice(end + 3)}`;
      }
      start = result.indexOf("<!--");
    }

    return result.replaceAll("-->", "");
  };

  const stripHtmlTags = (value: string): string => {
    let result = "";
    let inTag = false;

    for (const element of value) {
      const char = element;
      if (char === "<") {
        inTag = true;
        continue;
      }
      if (char === ">" && inTag) {
        inTag = false;
        continue;
      }
      if (!inTag) {
        result += char;
      }
    }

    return result;
  };

  const withoutComments = stripHtmlComments(html);
  const withoutTags = stripHtmlTags(withoutComments);

  return withoutTags
    .replaceAll(/\s+/g, " ") // Normalize whitespace
    .trim();
};

export const getChildByNodeName = (parent: NodeLike | undefined, nodeName: string): NodeLike[] => {
  return getSingleChildByNodeName(parent, nodeName)?.children ?? [];
};

export const getsearchPlatform = (authType: string) => {
  let searchPlatform: string;
  switch (authType) {
    case "I":
      searchPlatform = WEB_NON_LOGGED_IN_IDENTIFIED;
      break;
    case "A":
      searchPlatform = WEB_LOGGED_IN;
      break;
    default:
      searchPlatform = WEB_NON_LOGGED_IN;
  }
  return searchPlatform;
};

export const webFormData = (searchKey: string): Record<string, unknown> => {
  const searchPlatform = getsearchPlatform(getCookie("authType") ?? "");
  return {
    custType: getCookie("custType"),
    icorsOfferType: null,
    offerId: null,
    offerAmount: null,
    product: null,
    userEnteredAmount: null,
    cart: false,
    cardDesign: null,
    loginTimestamp: null,
    applicationID: null,
    prodCode: null,
    fasID: null,
    principleOutstandingAmt: null,
    principleOutStandingAmount: null,
    keyword_searched_searchWeb: searchKey,
    search_platform: searchPlatform,
    source: SOURCE,
  };
};

const redirectToWebFormNonLoggedin = async (url: string, form: Record<string, unknown>) => {
  const ssoRequest = {
    sso: {
      mobileNo: "",
      custId: "",
      accessToken: "",
      callbackUrl: "",
    },
    utm: null,
    myAccount: null,
    form,
  };
  const { encryptAESECB128 } = await loadCrypto();
  const searchData = await encryptAESECB128(JSON.stringify({ ssoRequest }));
  const pwaUrl = new URL(url);
  pwaUrl.searchParams.append("searchData", encodeURIComponent(searchData.toString()));
  globalThis.window.open(pwaUrl, "_self");
};

export const getCustomerDemogFromApp = () => {
  const windowWithTarsier = globalThis.window as Window & {
    tarsier?: { invokePrompt: (prompt: string, force?: boolean) => unknown };
  };
  const customerDemog = JSON.parse(
    (windowWithTarsier.tarsier?.invokePrompt(APP_PROMPTS.CUSTOMER_DEMOG_DETAILS, false) as
      string | undefined) ?? ""
  );
  return customerDemog;
};

export const isValidURL = (url: string) => {
  try {
    return Boolean(new URL(url));
  } catch (error) {
    LogUtils.error("Invalid URL:", error);
    return false;
  }
};

export const getFullUrl = (url: string, options?: { hash: boolean }): string => {
  const resolvedOptions = options ?? { hash: true };
  if (!isValidURL(url)) {
    const currentUrl = new URL(globalThis.window.location.href);
    const newUrl = new URL(`${currentUrl.protocol}//${currentUrl.hostname}${url}`);

    // Convert to string once for reuse
    let urlString = newUrl.toString();

    if (!resolvedOptions.hash) {
      return urlString.replace(newUrl.hash, "");
    }

    const src = getParams(APP_SRC);
    if (src === APP_M1) {
      const separator = urlString.includes("?") ? "&" : "?";
      const queryParam = `source=${src}`;
      urlString = `${urlString}${separator}${queryParam}`;
    }

    return urlString;
  }
  return url;
};
async function getSSOData(callbackUrl: string) {
  const { mobileNo, custId } = await getSessionData();
  const accessToken = globalThis.localStorage.getItem(COMMON_SSO_ACCESS_TOKEN);
  const fullUrl = getFullUrl(callbackUrl, { hash: false });
  const redirectionTime = `${Math.round(Date.now() / 1000)}`;
  const src = getParams(APP_SRC);
  const source = src === APP_M1 ? APP_M1 : APP_3in1;
  return {
    mobileNo,
    accessToken,
    callbackUrl: fullUrl,
    custId,
    redirectionTime,
    source,
  };
}

export const getPageType = (metaName: string): string => {
  // Check if running in a browser environment
  if (typeof document === "undefined" || !metaName) return "";
  try {
    const metaTag = document.querySelector(`meta[name="${metaName}"]`);
    if (!metaTag) {
      return "";
    }
    if (metaTag instanceof HTMLMetaElement) {
      return metaTag.content ?? "";
    }
    return metaTag.getAttribute("content") ?? "";
  } catch (error) {
    LogUtils.error(`Error getting page type for ${metaName}:`, error);
    return "";
  }
};

/**
 * This function retrieves UTM parameters from a cookie named "campaignCookie".
 * It also retrieves the value of a cookie named "lastClick" and the current window's URL.
 *
 * @returns An object containing the UTM parameters, the source URL, and the last click value.
 *
 * If any error occurs during the process (like the cookie not being present or not being a valid JSON), the function returns undefined.
 */
export function getUtmParamsFromCookie() {
  try {
    const cookie = sessionData?.campaignCookie;
    const lastClick = sessionData?.lastClick;
    const bflpageType = sessionData?.bfl_page_type ?? getPageType("bfl_page_type");
    return {
      utmSource: cookie?.utm_source,
      utmCampaign: cookie?.utm_campaign,
      utmMedium: cookie?.utm_medium,
      utmKeyword: cookie?.utm_keyword,
      utmContent: cookie?.utm_content,
      sourceUrl: globalThis.window.location.href,
      bflpageType: bflpageType,
      lastClick,
    };
  } catch (error) {
    LogUtils.error("Failed to get UTM params from cookie:", error);
    return undefined;
  }
}

export function getTnCVersion() {
  const tncVersionNumber = getPageType("tncVersionNumber");
  if (tncVersionNumber.length !== 0) {
    return {
      tncFlag: "1",
      tncVersionNumber: tncVersionNumber.toUpperCase(),
    };
  }
  return undefined;
}

export async function redirectToWebform(
  linkUrl: string,
  form: Record<string, unknown>,
  inAppFormLink?: string
): Promise<void> {
  const sso = await getSSOData(linkUrl);
  const utm = getUtmParamsFromCookie();
  const tnc = getTnCVersion();

  const ssoObject = {
    ssoRequest: {
      sso,
      utm,
      form,
      tnc,
    },
  };
  const ssoObjectSerialized = JSON.stringify(ssoObject);
  const { encryptWebform } = await loadCrypto();
  const ssoObjectEncrypted = await encryptWebform(ssoObjectSerialized);
  const currentUrl = globalThis.window.location.href;
  const currentUrlEncrypted = await encryptWebform(currentUrl);
  const src = getParams(APP_SRC);

  if (isOpenInApp() && inAppFormLink && form.applicationID && src !== APP_M1) {
    const { IN_APP_REDIRECTION, BAJAJ_SUPER_APP } = APP_PROMPTS;
    const appId =
      typeof form.applicationID === "string" || typeof form.applicationID === "number"
        ? String(form.applicationID)
        : "";
    const inAppRedirectionPrompt = `${IN_APP_REDIRECTION}${BAJAJ_SUPER_APP}${inAppFormLink}/${appId}`;

    const windowWithTarsier = globalThis.window as Window & {
      tarsier?: { invokePrompt: (prompt: string, force?: boolean) => unknown };
    };
    windowWithTarsier.tarsier?.invokePrompt(inAppRedirectionPrompt, true);
    return;
  }

  const url = new URL(sso.callbackUrl);
  url.searchParams.append("reqData", encodeURIComponent(ssoObjectEncrypted));
  url.searchParams.append("errorUrl", encodeURIComponent(currentUrlEncrypted));

  if (form.isVc) {
    globalThis.window.location.replace(url);
  } else {
    globalThis.window.open(url, "_self");
  }
}

export const redirectSearchWebForm = (url: string, searchKey: string, isLoggedIn: boolean) => {
  const form = webFormData(searchKey);
  if (isLoggedIn) {
    redirectToWebform(url, form);
  } else {
    redirectToWebFormNonLoggedin(url, form);
  }
};
/**
 * Helper function to get a single child by node name from a parent object
 * @param {any} parent - The parent object containing children array
 * @param {string} nodeName - The nodeName to search for
 * @returns {any} The first child object with matching nodeName, or undefined if not found
 * @example
 * getSingleChildByNodeName(globalHeader, "headerlogo")
 * getSingleChildByNodeName(bottomnavigationmobileStrip, "bottomNavigationMenu")
 */
export const getSingleChildByNodeName = (
  parent: NodeLike | undefined,
  nodeName: string
): NodeLike | undefined => {
  return parent?.children?.find((item: NodeLike) => item?.nodeName?.startsWith(nodeName));
};

export const createCacheAsync = async <Args extends unknown[], ReturnTypeValue>(
  key: string,
  fn: (...args: Args) => Promise<ReturnTypeValue>,
  ...args: Args
): Promise<ReturnTypeValue | undefined> => {
  let cachedResults = cache.get(fn);
  if (!cachedResults) {
    cachedResults = {};
    cache.set(fn, cachedResults);
  }
  if (!cachedResults[key]) {
    const result = Promise.resolve(fn(...args)).catch(() => {
      cachedResults[key] = undefined;
    });
    cachedResults[key] = result;
  }
  return cachedResults[key];
};

export const finalHandler = async (href: string): Promise<void> => {
  const callbackUrl = href;
  const formData = {
    cart: true,
  };
  redirectToWebform(callbackUrl.toString(), formData);
};

export const ssoHandlerMap = {
  mere: (data: unknown) => {
    // Handle Mere SSO logic
    LogUtils.log("Handling Mere SSO with data:", data);
  },
  default: (data: unknown) => {
    // Default SSO handler
    LogUtils.log("Handling default SSO with data:", data);
  },
  cart: finalHandler,
};

export const unixEpoch = (): string => `${Math.round(Date.now() / 1000)}`;

export async function redirectToDncPage() {
  const { cryptoAPICalledData } = await loadCrypto();
  const cryptoData = await cryptoAPICalledData();
  if (cryptoData) {
    globalThis.window.open(cryptoData?.DNC_PAGE_URL, "_self");
  }
}

export async function getVersionedCallbackUrl(mapping: SSOURLConfigs) {
  return mapping.newUrl;
}

export const parseSessionCookie = (cookie: string) => getCookie(cookie) ?? null;
export function readCookie(name: string): string | null {
  const cookieString = document.cookie;
  if (!cookieString) {
    return null;
  }

  const cookies = cookieString.split(";");
  for (const cookie of cookies) {
    const trimmedCookie = cookie.trim();
    const separatorIndex = trimmedCookie.indexOf("=");
    const cookieName = trimmedCookie.slice(0, separatorIndex);
    const cookieValue = trimmedCookie.slice(separatorIndex + 1);
    if (cookieName === name) {
      return cookieValue;
    }
  }

  return null;
}

export const getUserDetailsCookiesDecrypted = async () => {
  try {
    const bmallUserDetailsEncrypted = readCookie("bmall_user_details");
    if (bmallUserDetailsEncrypted) {
      const { cryptoAPICalledData, decryptAESCBC256 } = await loadCrypto();
      const cryptoData = await cryptoAPICalledData();
      if (!cryptoData?.REQ_ENC_DEC_PASS_PHRASE_EXP || !cryptoData?.REQ_ENC_DEC_IV_EXP) {
        LogUtils.error("Missing crypto data for decryption");
        return null;
      }
      const { REQ_ENC_DEC_PASS_PHRASE_EXP, REQ_ENC_DEC_IV_EXP } = cryptoData;
      const bmallUserDetailsPerpetual = await decryptAESCBC256(
        bmallUserDetailsEncrypted,
        REQ_ENC_DEC_PASS_PHRASE_EXP,
        REQ_ENC_DEC_IV_EXP
      );
      return JSON.parse(String(bmallUserDetailsPerpetual));
    }
    return null;
  } catch (error) {
    LogUtils.error("Error decrypting user details:", error);
    return null;
  }
};

export interface UserSessionDataTypes {
  custType: string;
  custId?: string;
  mobileNo: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  dob?: string;
  newDate: string;
  requestId: string;
  email: string;
  isOffers: string | boolean;
  city: string;
  dealerId: string;
  dealerName: string;
  cndFlag?: boolean;
  reKycFlag?: string;
  kycRenewalDate?: string;
  pinCode?: string;
  indivCorpFlag?: string;
  easyEmiProduct?: string;
  rekycflag?: string;
  kycProcess?: string;
}
// Get session data using key
export const getSessionData = async (): Promise<UserSessionDataTypes> => {
  const uniqueDigits = { uniqueDigits: "" };
  const sessionObj: UserSessionDataTypes | null = getCookie("userDetails")
    ? (JSON.parse(getCookie("userDetails") || "{}") as UserSessionDataTypes)
    : null;

  if (sessionObj) {
    const { decrypt } = await loadCrypto();
    const encryptedMobileNo = sessionObj.mobileNo;
    const encryptedDob = sessionObj.dob;
    const encryptedEmail = sessionObj.email;
    const salt = sessionObj.newDate;
    let decryptedMobileNo;
    if (uniqueDigits.uniqueDigits) {
      decryptedMobileNo = uniqueDigits.uniqueDigits;
    } else if (encryptedMobileNo) {
      decryptedMobileNo = await decrypt(encryptedMobileNo, salt);
    } else {
      decryptedMobileNo = "";
    }
    const decryptedDob = encryptedDob ? await decrypt(encryptedDob, salt) : "";
    const decryptedEmail = encryptedEmail ? await decrypt(encryptedEmail, salt) : "";
    // If mobileNo is not available in app, it will be fetched from customerdemog prompt
    const mobileNo =
      isOpenInApp() && !decryptedMobileNo ? getCustomerDemogFromApp().mobileNo : decryptedMobileNo;
    return {
      ...sessionObj,
      mobileNo: `${mobileNo}`,
      dob: `${decryptedDob}`,
      email: `${decryptedEmail}`,
    };
  }
  return {
    custType: "NTB",
    mobileNo: "",
    fullName: "",
    dob: "",
    firstName: "",
    lastName: "",
    newDate: "",
    requestId: "",
    email: "",
    isOffers: "",
    city: "",
    dealerId: "",
    dealerName: "",
    cndFlag: false, // Do Not Call (DNC) flag, variable name changed to cndFlag for security
    reKycFlag: "",
    kycRenewalDate: "",
    pinCode: "",
    indivCorpFlag: "",
  };
};

export const addStash = (url: string) => {
  let redirectionUrl: string;
  if (!url) {
    redirectionUrl = "/";
  } else if (url.startsWith("/")) {
    redirectionUrl = url;
  } else {
    redirectionUrl = `/${url}`;
  }
  return isOpenInApp() ? `/app-v1${redirectionUrl}` : redirectionUrl;
};

export const createEtfSlug = (etfName: string): string => {
  const parts: string[] = [];
  let currentPart = "";

  for (const character of etfName.toLowerCase().trim()) {
    const isAlphanumeric =
      (character >= "a" && character <= "z") || (character >= "0" && character <= "9");

    if (isAlphanumeric) {
      currentPart += character;
      continue;
    }

    if (currentPart) {
      parts.push(currentPart);
      currentPart = "";
    }
  }

  if (currentPart) {
    parts.push(currentPart);
  }

  const slug = parts.join("-");
  return slug.endsWith("-etf") ? slug : `${slug}-etf`;
};

export const getEtfRedirectPath = (etfName?: string, redirectURL?: string): string => {
  if (redirectURL) {
    return redirectURL;
  }

  if (!etfName) {
    return "";
  }

  return `investments/${createEtfSlug(etfName)}`;
};

export async function redirectToMyAccount(ssoPostUrl: string, linkUrl: string): Promise<void> {
  const sso = await getSSOData(linkUrl);
  const utm = getUtmParamsFromCookie();
  const { custType } = await getSessionData();
  const authType = getCookie("authType");
  const custFlag = custType === "NTB" ? "IN" : getCookie("custFlag");
  const ssoObject = {
    ssoRequest: {
      sso,
      utm,
      myAccount: {
        custType,
        custFlag,
        authType,
      },
    },
  };
  const ssoObjectSerialized = JSON.stringify(ssoObject);
  const { generateSalt, encrypt } = await loadCrypto();
  const salt = await generateSalt();
  const ssoObjectEncrypted = await encrypt(ssoObjectSerialized, salt);
  const form = document.createElement("form");
  form.method = "POST";
  form.action = getFullUrl(ssoPostUrl);

  const reqDataInput = document.createElement("input");
  reqDataInput.name = "reqData";
  reqDataInput.value = ssoObjectEncrypted;
  reqDataInput.type = "hidden";
  form.appendChild(reqDataInput);

  const reqKeyInput = document.createElement("input");
  reqKeyInput.name = "reqKey";
  reqKeyInput.value = salt;
  reqKeyInput.type = "hidden";
  form.appendChild(reqKeyInput);

  const errorUrl = document.createElement("input");
  errorUrl.name = "errorUrl";
  const encryptedErrorUrl = await encrypt(globalThis.window.location.href, salt);
  errorUrl.value = encodeURIComponent(encryptedErrorUrl);
  errorUrl.type = "hidden";
  form.appendChild(errorUrl);

  document.body.append(form);
  form.submit();
}

export const RedirectLinkFuncitonApp = (
  appurl: string = "",
  weburl: string = "",
  target: string = "_self",
  options?: {
    appendB2BClpUtm?: boolean;
    queryParams?: Record<string, string>;
  }
) => {
  const APP_CLP_UTM_BY_SLUG: Record<
    string,
    {
      utm_medium: string;
      utm_campaign: string;
    }
  > = {
    "electronics-on-emis": {
      utm_medium: "3in1_App_ElectronicsCLP_ElectronicsTab",
      utm_campaign: "3in1_App_ElectronicsCLP_ElectronicsTab_Top",
    },
    mobiles: {
      utm_medium: "3in1_App_MobilesCLP_MobilesTab",
      utm_campaign: "3in1_App_MobilesCLP_MobilesTab_Top",
    },
    television: {
      utm_medium: "3in1_App_TelevisionCLP_TelevisionTab",
      utm_campaign: "3in1_App_TelevisionCLP_TelevisionTab_Top",
    },
    "season-offers-for-you": {
      utm_medium: "3in1_App_OffersforyouCLP_OffersforyouTab",
      utm_campaign: "3in1_App_OffersforyouCLP_Offersforyou_Top",
    },
  };

  const getCurrentClpSlug = () => {
    if (!isBrowser()) return "";

    const pathSegments = globalThis.window.location.pathname
      .split("/")
      .filter(Boolean)
      .filter((segment) => !/^app-v\d+$/i.test(segment));

    return pathSegments[pathSegments.length - 1]?.toLowerCase() || "";
  };

  const appendInAppB2BUtmParams = (url: string) => {
    if (!isOpenInApp() || !url) return url;

    const currentSlug = getCurrentClpSlug();
    const utmConfig = APP_CLP_UTM_BY_SLUG[currentSlug];
    if (!utmConfig) return url;

    const isAbsoluteHttpUrl = /^https?:\/\//i.test(url);
    const isRootRelativeUrl = url.startsWith("/");
    if (!isAbsoluteHttpUrl && !isRootRelativeUrl) return url;

    try {
      const parsedUrl = isAbsoluteHttpUrl
        ? new URL(url)
        : new URL(url, globalThis.window.location.origin);

      parsedUrl.searchParams.set("utm_source", "3in1_App");
      parsedUrl.searchParams.set("utm_medium", utmConfig.utm_medium);
      parsedUrl.searchParams.set("utm_campaign", utmConfig.utm_campaign);

      if (isAbsoluteHttpUrl) {
        return parsedUrl.toString();
      }

      return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    } catch {
      return url;
    }
  };

  const appendQueryParams = (url: string, queryParams?: Record<string, string>) => {
    if (!url || !queryParams || Object.keys(queryParams).length === 0) return url;

    const isAbsoluteHttpUrl = /^https?:\/\//i.test(url);
    const isRootRelativeUrl = url.startsWith("/");
    if (!isAbsoluteHttpUrl && !isRootRelativeUrl) return url;

    try {
      const parsedUrl = isAbsoluteHttpUrl
        ? new URL(url)
        : new URL(url, globalThis.window.location.origin);

      Object.entries(queryParams).forEach(([key, value]) => {
        parsedUrl.searchParams.set(key, value);
      });

      if (isAbsoluteHttpUrl) {
        return parsedUrl.toString();
      }

      return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    } catch {
      return url;
    }
  };

  const webRedirectUrl = appendQueryParams(weburl, options?.queryParams);
  const baseRedirectUrl = appendQueryParams(appurl || webRedirectUrl, options?.queryParams);
  const appRedirectUrl =
    options?.appendB2BClpUtm === true ? appendInAppB2BUtmParams(baseRedirectUrl) : baseRedirectUrl;

  if (isOpenInApp()) {
    if (!appRedirectUrl.includes("bajajfinserv.in") && appRedirectUrl.startsWith("https:")) {
      globalThis.window.prompt(
        `openInAppWebViewActivity://{"url":"${appRedirectUrl}","title":"Bajaj Finserv"}`
      );
    } else if (appRedirectUrl.includes("STORE_LOCATOR")) {
      const normalizeInAppDeepLink = (link: string) => {
        return link.replace(/\\\\"/g, '\\"').replace(/\\"/g, '"');
      };
      globalThis.window.prompt(
        `${APP_PROMPTS.IN_APP_REDIRECTION}${normalizeInAppDeepLink(appRedirectUrl)}`
      );
    } else if (appRedirectUrl.includes("bajajsuperapp") && !appRedirectUrl.startsWith("https:")) {
      (
        globalThis.window as Window & { tarsier?: { invokePrompt?: (prompt: string) => void } }
      ).tarsier?.invokePrompt?.(`${APP_PROMPTS.IN_APP_REDIRECTION}${appRedirectUrl}`);
    } else {
      globalThis.window.open(appRedirectUrl, target);
    }
  } else {
    globalThis.window.open(webRedirectUrl, target);
  }
};

export const setAppCampaignCookie = (): void => {
  const campaignCookieValue = getCookie("campaignCookie");
  let existingCampaignCookie: Record<string, string> = {};
  if (campaignCookieValue) {
    try {
      existingCampaignCookie = JSON.parse(campaignCookieValue);
    } catch (e) {
      existingCampaignCookie = {};
    }
  }
  const campaign = {
    utm_medium: getParams("utm_medium") ?? existingCampaignCookie.utm_medium ?? "bfl",
    utm_campaign: getParams("utm_campaign") ?? existingCampaignCookie.utm_campaign ?? "NA",
    utm_keyword: getParams("utm_keyword") ?? existingCampaignCookie.utm_keyword ?? "NA",
    utm_source: getParams("utm_source") ?? existingCampaignCookie.utm_source ?? "organic_bfl",
    utm_content: getParams("utm_content") ?? existingCampaignCookie.utm_content ?? "NA",
  };
  setCookie("campaignCookie", 0, JSON.stringify(campaign));
};

const extractHttpsUrlFromDeepLink = (deepLink: string): string | null => {
  const httpsMatch = deepLink.match(/https:\/\/[^\s"'\\]+/);
  return httpsMatch?.[0] ?? null;
};

const appendRedirectUtmParams = (redirectUrl: URL, inApp: boolean): void => {
  if (inApp) {
    redirectUrl.searchParams.append("utm_source", "BFA_app");
    redirectUrl.searchParams.append("utm_medium", "BFA_app");
  } else {
    redirectUrl.searchParams.append("utm_source", "BFA_web");
    redirectUrl.searchParams.append("utm_medium", "web_Organic");
  }
  redirectUrl.searchParams.append("utm_campaign", window.location?.href.split("/").pop() ?? "");
};

const redirectInAppDeepLink = (deepLink: string): void => {
  const deepLinkPrompt = `${APP_PROMPTS.IN_APP_REDIRECTION}${deepLink}`;

  // Prefer embedded HTTPS + openInAppWebViewActivity (works on Android + iOS).
  // bajajsuperapp://tradingsso/...$https://... deeplinks often no-op on Android
  // even when tarsier.invokePrompt is available.
  const embeddedHttpsUrl = extractHttpsUrlFromDeepLink(deepLink);
  if (embeddedHttpsUrl) {
    try {
      const webViewUrl = new URL(embeddedHttpsUrl);
      appendRedirectUtmParams(webViewUrl, true);
      window.prompt(
        `openInAppWebViewActivity://{"url":"${webViewUrl.toString()}","title":"Bajaj Broking"}`
      );
      return;
    } catch {
      // fall through to deep link prompt
    }
  }

  const windowWithTarsier = window as Window & {
    tarsier?: {
      invokePrompt: (prompt: string, force?: boolean) => unknown;
    };
  };

  if (windowWithTarsier.tarsier?.invokePrompt) {
    windowWithTarsier.tarsier.invokePrompt(deepLinkPrompt, true);
    return;
  }

  window.prompt(deepLinkPrompt);
};

const openInAppWebView = (url: string): void => {
  window.prompt(`openInAppWebViewActivity://{"url":"${url}","title":"Bajaj Broking"}`);
};

const isBajajSuperAppDeepLink = (url: string): boolean =>
  url.includes("bajajsuperapp") && !url.startsWith("https:");

const redirectValidInAppUrl = (url: string, originalUrl: string, target: string): void => {
  if (!url.includes("bajajfinserv.in") && url.startsWith("https:")) {
    openInAppWebView(url);
    return;
  }

  if (isBajajSuperAppDeepLink(url)) {
    redirectInAppDeepLink(originalUrl);
    return;
  }

  window.open(url, target);
};

const redirectInvalidInAppUrl = (url: string): boolean => {
  if (!isBajajSuperAppDeepLink(url)) {
    openInAppWebView(url);
    return false;
  }

  redirectInAppDeepLink(url);
  return true;
};

// redirectWithUtmParam
export const redirectWithUtmParam = (url: string, redirectType: string = "false") => {
  const target = stringToBoolean(redirectType) ? "_blank" : "_self";

  try {
    const redirectUrl = new URL(url);
    const inApp = isOpenInApp();
    appendRedirectUtmParams(redirectUrl, inApp);

    const finalUrl = redirectUrl.toString();

    if (inApp) {
      redirectValidInAppUrl(finalUrl, url, target);
      return;
    }

    window.open(finalUrl, target);
  } catch (err) {
    console.error("Error constructing redirect URL:", err);

    if (isOpenInApp() && redirectInvalidInAppUrl(url)) {
      return;
    }

    window.open(url, target);
  }
};

export const formatMobileForDisplay = (mobile: string) => {
  if (!mobile || mobile.length < 4) {
    return "";
  }
  return `${mobile.slice(0, 2)}xxxxxx${mobile.slice(-2)}`;
};

export const isValidIndianMobileNumber = (mobileNumber: string): boolean => {
  return /^[6-9]\d{9}$/.test(mobileNumber);
};

export const hasAllRepeatingDigits = (mobileNumber: string): boolean => {
  return /^(\d)\1{9}$/.test(mobileNumber);
};

export const validateMobileNumber = (
  mobileNumber: string
): { isValid: boolean; errorMessage: string } => {
  if (!mobileNumber) {
    return { isValid: false, errorMessage: INVEST_NOW_MODAL_KEY.enterMobileNumber };
  } else if (mobileNumber.length !== 10) {
    return {
      isValid: false,
      errorMessage: INVEST_NOW_MODAL_KEY.invalidMobileNumber,
    };
  } else if (!isValidIndianMobileNumber(mobileNumber) || hasAllRepeatingDigits(mobileNumber)) {
    return {
      isValid: false,
      errorMessage: INVEST_NOW_MODAL_KEY.invalidMobileNumberFormat,
    };
  }

  return { isValid: true, errorMessage: "" };
};

/**
 * replacePlaceholders
 * @param template
 * @param values
 * @returns
 */
export function replacePlaceholders(template: string, values: Record<string, string>) {
  return template.replace(/\{([^{}]+)\}/g, (_: string, key: string) => {
    return values[key] ?? `{${key}}`;
  });
}

export const formatEmi = (emi: string | number | undefined) => {
  const num = typeof emi === "number" ? emi : Number(emi);
  return Number.isFinite(num) ? num.toLocaleString("en-IN") : "0";
};
